import { useCallback, useEffect, useId, useState } from 'react';
import { X } from 'lucide-react';
import { resolveSearchBucket } from './adminAnalyticsUtils';
import AdminEmailProbeReview from './AdminEmailProbeReview';
import {
  formatSearchTimestamp,
  getSearchTransportInfo,
  resolveClientHash,
} from './adminSearchDetailUtils';
import { downloadBundleFromAdminSearch } from './adminSearchBundle';

const BADGE_BASE = 'inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider';

function TransportBadge({ transport }) {
  if (transport.kind === 'sse') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className={`${BADGE_BASE} border-cyan-500/40 bg-cyan-950/50 text-cyan-300`}>
          {transport.label}
        </span>
        {transport.eventCount != null ? (
          <span className="text-xs font-semibold text-slate-300">
            {transport.eventCount} event{transport.eventCount === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-xs text-slate-500">Event count pending stream close</span>
        )}
      </div>
    );
  }
  if (transport.kind === 'ai_json') {
    return (
      <span className={`${BADGE_BASE} border-violet-500/40 bg-violet-950/50 text-violet-300`}>
        {transport.label}
      </span>
    );
  }
  if (transport.kind === 'json') {
    return (
      <span className={`${BADGE_BASE} border-emerald-500/40 bg-emerald-950/50 text-emerald-300`}>
        {transport.label}
      </span>
    );
  }
  return (
    <span className={`${BADGE_BASE} border-slate-600 bg-slate-800 text-slate-400`}>{transport.label}</span>
  );
}

export default function AdminSearchDetailDrawer({ search, onClose }) {
  const titleId = useId();
  const [bundleBusy, setBundleBusy] = useState(false);
  const [bundleError, setBundleError] = useState('');
  const close = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  useEffect(() => {
    if (!search) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [search]);

  useEffect(() => {
    if (!search) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search, close]);

  const handleDownloadBundle = useCallback(async () => {
    if (!search) return;
    setBundleBusy(true);
    setBundleError('');
    try {
      const out = await downloadBundleFromAdminSearch(search);
      if (!out) {
        setBundleError('No SSE events stored for this search — run a live scan first.');
      }
    } catch (exc) {
      setBundleError(String(exc?.message || exc));
    } finally {
      setBundleBusy(false);
    }
  }, [search]);

  if (!search) return null;

  const transport = getSearchTransportInfo(search);
  const bucket = resolveSearchBucket(search);
  const pathLabel = search.path || search.full_url || '—';
  const statusCode = search.response_meta?.status_code;
  const isEmail = bucket === 'email';
  const isPreprocess = bucket === 'ai_preprocess';
  const eventCount = Array.isArray(search?.result_json?.events)
    ? search.result_json.events.length
    : 0;
  const canBundle = eventCount > 0;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        aria-label="Close search detail overlay"
        onClick={close}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={`relative flex h-full w-full flex-col border-l border-slate-700 bg-slate-900 shadow-2xl shadow-black/40 ${
          isEmail ? 'max-w-2xl' : 'max-w-lg'
        }`}
      >
        <header className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-700 px-5 py-4">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Investigation detail</p>
            <h2 id={titleId} className="mt-1 truncate text-sm font-bold text-slate-100" title={pathLabel}>
              {pathLabel}
            </h2>
            <span className="mt-2 inline-flex rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase text-slate-300">
              {bucket}
            </span>
          </div>
          <button
            type="button"
            onClick={close}
            className="rounded-lg border border-slate-600 p-2 text-slate-400 transition-colors hover:border-slate-500 hover:bg-slate-800 hover:text-slate-100"
            aria-label="Close search detail"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
          <section className="rounded-xl border border-slate-700 bg-slate-950/50 p-3">
            <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">
              Artifact bundle
            </h3>
            <p className="mt-1 text-[11px] leading-relaxed text-slate-400">
              ZIP with debug JSON, complete logs, and PDF report reconstructed from stored SSE events
              ({eventCount} event{eventCount === 1 ? '' : 's'}).
            </p>
            <button
              type="button"
              disabled={!canBundle || bundleBusy}
              onClick={handleDownloadBundle}
              data-testid="admin-search-download-bundle"
              className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-[11px] font-black uppercase tracking-wider text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {bundleBusy ? 'Packing…' : 'Download Bundle'}
            </button>
            {bundleError ? (
              <p className="mt-2 text-[11px] font-semibold text-rose-400">{bundleError}</p>
            ) : null}
          </section>

          <section>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">ID &amp; timestamp</h3>
            <dl className="mt-2 space-y-2 text-sm">
              <div>
                <dt className="text-[10px] font-bold uppercase text-slate-500">Investigation ID</dt>
                <dd className="mt-0.5 font-mono text-xs text-slate-200">{search.id || '—'}</dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase text-slate-500">Recorded at</dt>
                <dd className="mt-0.5 text-slate-200">{formatSearchTimestamp(search.ts)}</dd>
                {search.ts ? (
                  <dd className="mt-0.5 font-mono text-[10px] text-slate-500">{search.ts}</dd>
                ) : null}
              </div>
            </dl>
          </section>

          <section>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Query (GDPR redacted)</h3>
            <pre className="mt-2 max-h-48 overflow-auto rounded-lg border border-slate-700 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-emerald-300/90">
              {JSON.stringify(search.query || {}, null, 2)}
            </pre>
          </section>

          <section>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Transport &amp; execution</h3>
            <div className="mt-2 space-y-2">
              <TransportBadge transport={transport} />
              {statusCode != null ? (
                <p className="text-xs text-slate-400">
                  HTTP <span className="font-mono font-semibold text-slate-300">{statusCode}</span>
                  {search.response_meta?.mimetype ? (
                    <span className="text-slate-500"> · {search.response_meta.mimetype}</span>
                  ) : null}
                </p>
              ) : null}
            </div>
          </section>

          <section>
            <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Client correlation</h3>
            <div className="mt-2">
              <span className={`${BADGE_BASE} border-slate-600 bg-slate-800 font-mono normal-case tracking-normal text-slate-400`}>
                {resolveClientHash(search)}
              </span>
              <p className="mt-2 text-[11px] leading-snug text-slate-500">
                IP Anonimizat prin SHA-256 (GDPR Compliant)
              </p>
            </div>
          </section>

          {isEmail ? <AdminEmailProbeReview search={search} /> : null}

          {isPreprocess ? (
            <section>
              <h3 className="text-[10px] font-black uppercase tracking-wider text-slate-500">Preprocess payload</h3>
              <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-slate-700 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-violet-200/90">
                {JSON.stringify(search.result_json || {}, null, 2)}
              </pre>
            </section>
          ) : null}
        </div>
      </aside>
    </div>
  );
}
